import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@rbilabs/universal-components';
import { spacing } from '@rbilabs/universal-components/themes';
import { useLocalSearchParams, useRouter, useUnstableGlobalHref } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { ActivityIndicator, Linking, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { WebView as RNWebView } from 'react-native-webview';

import { Camera } from 'components/camera';
import { FlipMode } from 'components/camera/types';
import CloseButton from 'components/close-button';
import { Helmet } from 'components/helmet';
import { ModalAuth } from 'components/modal-auth';
import { toaster } from 'components/toaster/toaster';
import { useCreateLoginForVendorMutation } from 'generated/graphql-gateway';
import { useModalAnalyticsAppearanceEvent } from 'hooks/use-modal-analytics-appearance-event';
import { EventTypes, logEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { ModalAuthTrigger } from 'state/auth/types';
import { t } from 'state/intl/translations';
import { primitive } from 'styles/constants/primitives';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { getDeviceId } from 'utils/device-id';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { isExternalLink } from 'utils/is-external-link';
import { logger } from 'utils/logger';
import { routes } from 'utils/routing';

import { WebView } from './components/webview';
import { postMessageToIframe } from './components/webview/listeners';
import {
  CameraConfiguration,
  CampaignMessage,
  CampaignRouteParams,
  HandlersType,
  ScreenOrientation,
  ToastVariant,
} from './types';
import { useGetExternalCampaignBySlug } from './use-get-external-campaign-by-slug';
import { useScreenOrientation } from './use-screen-orientation';
import { StartStopSensorsConfig, useSensors } from './use-sensors';
import {
  appMessages,
  downloadFileFromUrl,
  handleShare,
  isCampaignActive,
  normalizeCameraOptions,
} from './utils';

const styles = StyleSheet.create({
  loadingAnimation: {
    zIndex: 9999,
    backgroundColor: primitive.bk.$white,
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
});

// TODO: Rename to a more standardized format, such as CampaignsPage.
export const Campaigns = () => {
  const [showCamera, setShowCamera] = useState<CameraConfiguration | null>(null);
  const [onDismissRoute, setOnDismissRoute] = useState<string>('');
  const [isVendorLoginComplete, setVendorLoginComplete] = useState(false);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [isWebViewLoading, setIsWebViewLoading] = useState(true);
  const [isCampaignLoadingFinished, setIsCampaignLoadingFinished] = useState(false);

  const params = useLocalSearchParams<CampaignRouteParams>();
  const query = useUnstableGlobalHref()?.split('?')?.[1];
  const queryParam = query ? `?${query}` : '';
  const slug = params.campaignSlug?.toLowerCase() || '';
  const webViewRef = useRef<RNWebView>(null);
  const platformMessageSentRef = useRef(false);
  const deviceIdMessageSentRef = useRef(false);
  const safeAreaInsetsMessageSentRef = useRef(false);
  const jwtRef = useRef<string>('');
  const campaignUrlRef = useRef<string | undefined>(undefined);
  const insets = useSafeAreaInsets();
  const router = useRouter();

  const {
    user,
    setModalAuthState,
    loading: authLoading,
    openSignInModal,
    closeModalAuth,
    modalAuthState,
  } = useAuthContext();

  const { campaign, isLoading: isLoadingCampaignFromSanity } = useGetExternalCampaignBySlug({
    slug,
  });

  const handlePostMessageToIframe = useCallback(
    (message: string) => postMessageToIframe(webViewRef, message, campaignUrlRef.current),
    []
  );

  const handleOnOrientationChange = useCallback(
    (orientation: ScreenOrientation) =>
      handlePostMessageToIframe(appMessages.screenOrientation(orientation)),
    [handlePostMessageToIframe]
  );

  const handleOnPhotoTaken = useCallback(
    (photo: string) => {
      handlePostMessageToIframe(appMessages.sendCameraResult(photo));
      setShowCamera(null);
    },
    [handlePostMessageToIframe]
  );

  const onCameraAccessDenied = useCallback(() => {
    handlePostMessageToIframe(appMessages.sendCameraResult('CAMERA_ACCESS_DENIED'));
  }, [handlePostMessageToIframe]);

  const [
    createLoginForVendor,
    { loading: loginForVendorIsLoading },
  ] = useCreateLoginForVendorMutation();

  const { orientation, setOrientation, setOrientationUnlocked } = useScreenOrientation({
    onOrientationChange: handleOnOrientationChange,
  });

  const { startStopSensors, checkSensorsAvailability } = useSensors({ handlePostMessageToIframe });

  /*
   * Handlers for messages sent from the iframe
   * Param handlers are used for messages that have a param, and we use regex then to match the messages and get the param
   * ie: "APP::NAVIGATE_TO::/home" should match the handler for "APP::NAVIGATE_TO::(.*)" and the param should be "/home"
   * Base handlers are used for messages that don't have a param and can be matched directly
   * */
  const handlers: HandlersType = useMemo(() => {
    const paramHandlers = {
      [CampaignMessage.navigateTo]: (route: string) => {
        if (isExternalLink(route)) {
          Linking.openURL(route);
        } else {
          router.navigate(route);
        }
      },
      [CampaignMessage.onDismiss]: (route: string) => setOnDismissRoute(route),
    };

    const baseHandlers = {
      [CampaignMessage.initiateLogin]: () => {
        try {
          if (!jwtRef.current) {
            openSignInModal({
              shouldWaitLoginForVendor: true,
              shouldDisableHeadlineLinks: true,
              trigger: ModalAuthTrigger.Campaign,
            });
          }
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process campaign login',
            campaignMessage: CampaignMessage.initiateLogin,
          });
        }
      },
      [CampaignMessage.loginComplete]: () => {
        try {
          closeModalAuth();
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process campaign login drawer close',
            campaignMessage: CampaignMessage.loginComplete,
          });
        }
      },
      [CampaignMessage.requestDevice]: () => {
        try {
          handlePostMessageToIframe(appMessages.device(getDeviceId()));
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process campaign request for device id',
            campaignMessage: CampaignMessage.requestDevice,
          });
        }
      },
      [CampaignMessage.requestUser]: () => {
        try {
          handlePostMessageToIframe(appMessages.user(jwtRef.current));
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process campaign user request',
            campaignMessage: CampaignMessage.requestUser,
          });
        }
      },
      [CampaignMessage.requestPlatform]: () => {
        try {
          handlePostMessageToIframe(appMessages.platform);
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process campaign platform request',
            campaignMessage: CampaignMessage.requestPlatform,
          });
        }
      },
      [CampaignMessage.showOneTrustDialog]: () => {
        try {
          window.OneTrust.ToggleInfoDisplay();
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process OneTrust dialog request',
            campaignMessage: CampaignMessage.showOneTrustDialog,
          });
        }
      },
      [CampaignMessage.share]: async (data: string, message: string) => {
        try {
          await handleShare({ data, message });
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process campaign share',
            campaignMessage: CampaignMessage.share,
          });
        }
      },
      [CampaignMessage.download]: async (url: string, messageParam: string) => {
        try {
          const successDownloadMessage = {
            message: messageParam || t('fileDownloadedMessage'),
            title: t('fileDownloadedTitle'),
          };
          await downloadFileFromUrl({ url, successDownloadMessage });
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process campaign asset download',
            campaignMessage: CampaignMessage.download,
          });
        }
      },
      [CampaignMessage.startStopSensors]: async (sensorConfigString: string) => {
        try {
          const areSensorsAvailable = await checkSensorsAvailability();

          if (!areSensorsAvailable) {
            handlePostMessageToIframe(appMessages.sensorsAvailability(areSensorsAvailable));
            return;
          }

          const sensors = JSON.parse(sensorConfigString) as StartStopSensorsConfig;
          startStopSensors(sensors);
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process campaign sensor config update',
            campaignMessage: CampaignMessage.startStopSensors,
          });
        }
      },
      [CampaignMessage.checkSensorsAvailability]: async () => {
        try {
          const areSensorsAvailable = await checkSensorsAvailability();
          handlePostMessageToIframe(appMessages.sensorsAvailability(areSensorsAvailable));
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process campaign sensor availability check',
            campaignMessage: CampaignMessage.checkSensorsAvailability,
          });
        }
      },
      [CampaignMessage.requestOrientation]: async () => {
        try {
          handlePostMessageToIframe(appMessages.screenOrientation(orientation));
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process campaign request orientation',
            campaignMessage: CampaignMessage.requestOrientation,
          });
        }
      },
      [CampaignMessage.setOrientation]: async (orientationRequested: ScreenOrientation) => {
        try {
          if (!orientationRequested) {
            return;
          }
          await setOrientation(orientationRequested);
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process campaign orientation update',
            campaignMessage: CampaignMessage.setOrientation,
          });
        }
      },
      [CampaignMessage.setOrientationUnlocked]: async () => {
        try {
          await setOrientationUnlocked();
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process campaign orientation unlock',
            campaignMessage: CampaignMessage.setOrientationUnlocked,
          });
        }
      },
      [CampaignMessage.postEvent]: (eventName: string, jsonPayload: string) => {
        try {
          logEvent(eventName, EventTypes.Other, JSON.parse(jsonPayload));
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process campaign event post',
            campaignMessage: CampaignMessage.postEvent,
          });
        }
      },
      [CampaignMessage.getLocalStorage]: () => {
        try {
          const localStorage = syncStorage.getObject<Record<string, any>>(
            SyncStorageKey.CAMPAIGNS_LOCAL_STORAGE
          );
          const campaignStorage = localStorage?.[slug] || {};
          handlePostMessageToIframe(appMessages.localStorage(campaignStorage));
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to get campaign local storage',
            campaignMessage: CampaignMessage.getLocalStorage,
          });
        }
      },
      [CampaignMessage.setLocalStorage]: (campaignLocalStorage: string) => {
        try {
          // Get the existing campaigns from local storage
          const prevCampaignsLocalStorage =
            syncStorage.getObject<Record<string, any>>(SyncStorageKey.CAMPAIGNS_LOCAL_STORAGE) ||
            {};
          const updatedLocalStorage = {
            ...prevCampaignsLocalStorage,
            [slug]: campaignLocalStorage,
          };
          syncStorage.setObject(SyncStorageKey.CAMPAIGNS_LOCAL_STORAGE, updatedLocalStorage);
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process campaign set local storage',
            campaignMessage: CampaignMessage.setLocalStorage,
          });
        }
      },
      [CampaignMessage.requestCameraAccess]: (cameraAccess: string, rawOptions: string) => {
        try {
          const flip = cameraAccess.split('::');
          let parsedOptions = '';
          if (rawOptions) {
            const opt = rawOptions?.split('::');
            parsedOptions = opt[opt.length - 1];
          }
          const options = normalizeCameraOptions(parsedOptions);
          setShowCamera({
            flip: flip[flip.length - 1].toLowerCase() as FlipMode,
            options,
          });
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process request Camera',
            campaignMessage: CampaignMessage.requestCameraAccess,
          });
        }
      },
      [CampaignMessage.requestSafeAreaInsets]: () => {
        try {
          handlePostMessageToIframe(appMessages.safeAreaInsets(insets));
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process campaign request safe area insets',
            campaignMessage: CampaignMessage.requestSafeAreaInsets,
          });
        }
      },
      [CampaignMessage.loadingFinished]: () => {
        try {
          setIsCampaignLoadingFinished(true);
          handlePostMessageToIframe(appMessages.loadingFinished);
        } catch (error) {
          logger.error({
            error,
            message: 'Failed to process campaign loading finished',
            campaignMessage: CampaignMessage.loadingFinished,
          });
        }
      },
      [CampaignMessage.toast]: (variant: ToastVariant, message: string) => {
        try {
          const toastMethods = {
            INFO: toaster.informative,
            SUCCESS: toaster.positive,
            ERROR: toaster.negative,
            NEUTRAL: toaster.neutral,
          };

          const showToast = toastMethods[variant];

          showToast(message);
        } catch (error) {
          logger.error({
            error,
            toastVariant: variant,
            toastMessage: message,
            message: 'Failed to process campaign toast',
            campaignMessage: CampaignMessage.toast,
          });
        }
      },
    };

    return { ...baseHandlers, ...paramHandlers };
  }, [
    router,
    openSignInModal,
    closeModalAuth,
    handlePostMessageToIframe,
    checkSensorsAvailability,
    startStopSensors,
    orientation,
    setOrientation,
    setOrientationUnlocked,
    slug,
    insets,
  ]);

  const handleMessageReceived = useCallback(
    (message: string) => {
      if (handlers[message]) {
        handlers[message]();
        return;
      }

      /*
       *  For messages with param, we need to match the message to the handler using a regex.
       *  ie: the message "APP::NAVIGATE_TO::/home" should match the handler for "APP::NAVIGATE_TO::(.*)"
       *  The param is the first match in the regex
       */
      for (const handler in handlers) {
        if (handler && typeof message === 'string') {
          const match = message?.match(new RegExp(handler));
          const params = match?.splice(1);

          if (match && params?.length) {
            if (typeof handlers[handler] === 'function') {
              handlers[handler](...params);
              return;
            }
          }
        }
      }
    },
    [handlers]
  );

  // If the external campaign sets a route to navigate to on dismiss, navigate to that route.
  // Otherwise, if the campaign has a dismiss button, go back to the previous or base route.
  const handleOnDismiss = useCallback(() => {
    if (onDismissRoute) {
      router.replace(onDismissRoute);
      return;
    }

    if (campaign?.displayInitialDismissButton) {
      if (router.canGoBack()) {
        router.back();
      } else {
        router.replace(routes.base);
      }
    }
  }, [onDismissRoute, campaign?.displayInitialDismissButton, router]);

  const isAuthenticated = !!user?.cognitoId;
  // The campaign can set a route to navigate to on dismiss, or we can set a default route. If neither is set, we don't allow dismiss
  const allowsDismiss = !!campaign?.displayInitialDismissButton || !!onDismissRoute;
  const isCampaignReady = !isWebViewLoading && !authLoading;

  useEffect(() => {
    campaignUrlRef.current = campaign?.externalUrl;
  }, [campaign?.externalUrl]);

  // Navigate to the base route if the campaign is unavailable or inactive
  useEffect(() => {
    const isCampaignUnavailable = !isLoadingCampaignFromSanity && !campaign;

    const isCampaignInactive =
      campaign &&
      !isCampaignActive({
        startDateString: campaign.startDate,
        endDateString: campaign.endDate,
        timeZone: campaign.timezone!,
      });

    const isCampaignMissingRequiredFields =
      campaign && (!campaign?.externalUrl || !campaign?.vendor);

    if (isCampaignUnavailable || isCampaignInactive || isCampaignMissingRequiredFields) {
      if (isCampaignMissingRequiredFields) {
        logger.error({
          message: 'Campaign is missing required fields externalUrl or vendor',
          campaign,
        });
      }

      router.replace(routes.base);
    }
  }, [campaign, isLoadingCampaignFromSanity, router]);

  // Controls the initial loading state of the WebView
  useEffect(() => {
    if (isIframeLoaded && campaign && isWebViewLoading) {
      setIsWebViewLoading(campaign.useNativeLoader ? !isCampaignLoadingFinished : false);
    }
  }, [isIframeLoaded, campaign, isWebViewLoading, isCampaignLoadingFinished]);

  // If there is a user logged in, we need to create a login for the vendor
  // This login will be used to identify the user in the iframe
  // Once the login is created, we send the APP::USER message to the iframe
  useEffect(() => {
    let isMounted = true;

    if (!isCampaignReady || isVendorLoginComplete) {
      return;
    }

    if (!isAuthenticated) {
      // Post the APP::USER message as empty string to the iframe to indicate no user is logged in
      handlePostMessageToIframe(appMessages.user(''));
      return;
    }

    handlePostMessageToIframe(appMessages.loading(true));

    if (isMounted && loginForVendorIsLoading) {
      setModalAuthState({ loginForVendorIsLoading });
    }

    createLoginForVendor({
      variables: {
        vendor: campaign.vendor,
      },
    }).then(({ data }) => {
      if (!isMounted) {
        return;
      }
      setVendorLoginComplete(true);
      setModalAuthState({ loginForVendorIsLoading: false });
      jwtRef.current = data?.createLoginForVendor || '';
      handlePostMessageToIframe(appMessages.user(jwtRef.current));
      handlePostMessageToIframe(appMessages.loading(false));
    });

    return () => {
      isMounted = false;
    };
  }, [
    campaign?.vendor,
    createLoginForVendor,
    handlePostMessageToIframe,
    isAuthenticated,
    isCampaignReady,
    isVendorLoginComplete,
    loginForVendorIsLoading,
    setModalAuthState,
  ]);

  // Send the platform and version only one time and once the campaign is ready
  useEffect(() => {
    if (!isCampaignReady || !campaign?.vendor || platformMessageSentRef.current) {
      return;
    }

    handlePostMessageToIframe(appMessages.platform);
    platformMessageSentRef.current = true;
  }, [campaign?.vendor, handlePostMessageToIframe, isCampaignReady]);

  useEffect(() => {
    // Send the device id only one time and once the campaign is ready
    if (isWebViewLoading || deviceIdMessageSentRef.current) {
      return;
    }
    handlePostMessageToIframe(appMessages.device(getDeviceId()));
    deviceIdMessageSentRef.current = true;
  }, [handlePostMessageToIframe, isWebViewLoading]);

  useEffect(() => {
    // Send the safe area insets whenever the campaign is ready or the insets change
    if (isWebViewLoading || safeAreaInsetsMessageSentRef.current) {
      return;
    }

    handlePostMessageToIframe(appMessages.safeAreaInsets(insets));
  }, [handlePostMessageToIframe, insets, isWebViewLoading]);

  useModalAnalyticsAppearanceEvent(campaign?.externalUrl && 'Show External Campaign Modal');

  return (
    <>
      <Helmet title={campaign?.title}>
        <meta name="description" content={campaign?.description || ''} />
        <noscript>{campaign?.seoHtml?.code}</noscript>
      </Helmet>
      <StatusBar hidden />
      {(allowsDismiss || !isIframeLoaded) && (
        <CloseButtonWrapper style={{ top: insets.top }}>
          <CloseButton
            onClick={handleOnDismiss}
            testID="modal-close-button"
            backgroundColor="white"
            {...hiddenAccessibilityPlatformProps}
          />
        </CloseButtonWrapper>
      )}
      {isWebViewLoading && (
        <View style={styles.loadingAnimation}>
          <ActivityIndicator size="large" color={primitive.bk.$fieryRed} />
        </View>
      )}
      {campaign?.externalUrl && (
        <WebView
          testID="external-campaign"
          externalUrl={campaign.externalUrl}
          queryParam={queryParam}
          onMessageReceived={handleMessageReceived}
          onLoadEnd={() => setIsIframeLoaded(true)}
          webViewRef={webViewRef}
          backgroundColor={campaign.backgroundColor?.hex || ''}
        />
      )}
      {
        // Display the authentication modal ONLY if it IS within a campaign context.
        modalAuthState.trigger === ModalAuthTrigger.Campaign && <ModalAuth />
      }
      {showCamera !== null && (
        <Camera
          onClose={() => setShowCamera(null)}
          callback={handleOnPhotoTaken}
          {...showCamera}
          onCameraAccessDenied={onCameraAccessDenied}
        />
      )}
    </>
  );
};

const CloseButtonWrapper = Box.withConfig({
  position: 'absolute',
  right: spacing.$1,
  zIndex: 1,
});
